import * as React from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "./chart";
import { cn } from "@/lib/utils";

export interface BarChartProps {
  data: any[];
  className?: string;
  config?: any;
  variant?: "default" | "animated" | "interactive" | "minimal" | "gradient" | "stacked" | "grouped";
  theme?: "light" | "dark" | "minimal";
  responsive?: boolean;
  accessible?: boolean;
  gradient?: boolean;
  stacked?: boolean;
  grouped?: boolean;
  horizontal?: boolean;
  dataKeys?: string[];
  colors?: string[];
  strokeWidth?: number;
  radius?: number | [number, number, number, number];
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  height?: number | string;
  width?: number | string;
}

export function BarChart({
  data,
  className,
  config = {},
  variant = "default",
  theme = "light",
  responsive = true,
  accessible = true,
  gradient = true,
  stacked = false,
  grouped = false,
  horizontal = false,
  dataKeys = ["value"],
  colors = [
    "hsl(221.2 83.2% 53.3%)",
    "hsl(217.2 91.2% 59.8%)",
    "hsl(214.3 31.8% 91.4%)",
    "hsl(210 40% 96%)",
  ],
  strokeWidth = 2,
  radius = 4,
  showGrid = true,
  showLegend = false,
  showTooltip = true,
  height,
  width,
  ...props
}: BarChartProps) {
  const chartId = React.useId();
  
  // Generate gradient IDs
  const gradientIds = dataKeys.map((_, index) => `bar-gradient-${chartId}-${index}`);
  
  // Determine colors based on theme
  const getThemeColors = () => {
    switch (theme) {
      case "dark":
        return [
          "#60a5fa",
          "#34d399",
          "#fbbf24",
          "#f87171",
        ];
      case "minimal":
        return [
          "#6b7280",
          "#9ca3af",
          "#d1d5db",
          "#e5e7eb",
        ];
      default:
        return colors;
    }
  };

  const themeColors = getThemeColors();

  // Determine variant-specific props
  const getVariantProps = () => {
    switch (variant) {
      case "animated":
        return {
          className: cn(className, "wl-bar-chart-animated"),
          animationDuration: 1500,
          animationEasing: "ease-out",
        };
      case "interactive":
        return {
          className: cn(className, "wl-bar-chart-interactive"),
        };
      case "minimal":
        return {
          className: cn(className, "wl-bar-chart-minimal"),
          radius: 0,
          strokeWidth: 1,
        };
      case "gradient":
        return {
          className: cn(className, "wl-bar-chart-gradient"),
          gradient: true,
        };
      default:
        return {
          className,
        };
    }
  };

  const variantProps = getVariantProps();

  return (
    <div
      className={cn(
        "wl-bar-chart",
        "wl-bar-chart-theme",
        `wl-bar-chart-theme--${theme}`,
        variant !== "default" && `wl-bar-chart-${variant}`,
        responsive && "wl-bar-chart-responsive",
        accessible && "wl-bar-chart-accessible",
        horizontal && "wl-bar-chart-horizontal",
        stacked && "wl-bar-chart-stacked",
        grouped && "wl-bar-chart-grouped",
        className
      )}
      data-chart-type="bar"
      style={{ height, width }}
      {...props}
    >
      <ChartContainer config={config}>
        <RechartsBarChart
          data={data}
          layout={horizontal ? "horizontal" : "vertical"}
          margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
          {...variantProps}
        >
          {gradient && (
            <defs>
              {dataKeys.map((_, index) => (
                <linearGradient
                  key={gradientIds[index]}
                  id={gradientIds[index]}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={themeColors[index % themeColors.length]}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="100%"
                    stopColor={themeColors[index % themeColors.length]}
                    stopOpacity={0.4}
                  />
                </linearGradient>
              ))}
            </defs>
          )}
          
          {!horizontal ? (
            <>
              <XAxis
                dataKey="name"
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
            </>
          ) : (
            <>
              <XAxis
                type="number"
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                dataKey="name"
                type="category"
                tickLine={false}
                axisLine={false}
                width={140}
              />
            </>
          )}
          
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(0,0,0,0.1)"
              vertical={horizontal}
              horizontal={!horizontal}
            />
          )}
          
          {showTooltip && (
            <ChartTooltip
              content={<ChartTooltipContent />}
              cursor={false}
            />
          )}
          
          {showLegend && (
            <ChartLegend content={<ChartLegendContent />} />
          )}
          
          {dataKeys.map((dataKey, index) => (
            <Bar
              key={dataKey}
              dataKey={dataKey}
              fill={
                gradient
                  ? `url(#${gradientIds[index]})`
                  : themeColors[index % themeColors.length]
              }
              radius={Array.isArray(radius) ? radius : [radius, radius, 0, 0]}
              stroke={themeColors[index % themeColors.length]}
              strokeWidth={strokeWidth}
              stackId={stacked ? "a" : undefined}
              animationDuration={variant === "animated" ? 1500 : undefined}
              animationEasing={variant === "animated" ? "ease-out" : undefined}
            />
          ))}
        </RechartsBarChart>
      </ChartContainer>
    </div>
  );
}

// Export for convenience
export { BarChart as default }; 